import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Users, Brain, TrendingUp, Info, Play, PowerOff } from 'lucide-react';
import { CallerCallsModal } from './CallerCallsModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// DISC quadrant metadata — used for the donut + top-caller badges.
const DISC_META = {
  D: { name: 'Dominance',      color: '#EF4444', hint: 'Direct, results-focused, blunt.' },
  I: { name: 'Influence',      color: '#F59E0B', hint: 'Enthusiastic, chatty, warm.' },
  S: { name: 'Steadiness',     color: '#10B981', hint: 'Calm, patient, listens well.' },
  C: { name: 'Conscientious',  color: '#3B82F6', hint: 'Analytical, cautious, data-driven.' },
};

const ENGAGEMENT_LABELS = {
  listened: 'Listened attentively',
  interrupted_often: 'Interrupted often',
  decisive: 'Decisive',
  hesitant: 'Hesitant',
  price_focused: 'Price-focused',
  relationship_focused: 'Relationship-focused',
};

const MOOD_META = {
  neutral: { label: 'Neutral', color: '#94A3B8' },
  warm: { label: 'Warm', color: '#10B981' },
  frustrated: { label: 'Frustrated', color: '#EF4444' },
  curious: { label: 'Curious', color: '#3B82F6' },
  resistant: { label: 'Resistant', color: '#F97316' },
};

// NOTE: don't reach into localStorage for a token — the app keeps the JWT
// in-memory only and installs it globally via
// `axios.defaults.headers.common['Authorization']`. Passing an explicit
// `Authorization: Bearer null` header (which happened when this file used
// `localStorage.getItem('token')`) overrides the good default with a
// broken value, backend 401s, the interceptor logs the user out, and the
// app kicks them back to /select-mode. Just let axios use its defaults.

const DiscDonut = ({ percent }) => {
  // Build stacked conic-gradient. Order: D, I, S, C.
  const stops = [];
  let cursor = 0;
  ['D', 'I', 'S', 'C'].forEach((k) => {
    const pct = percent[k] || 0;
    if (pct > 0) {
      stops.push(`${DISC_META[k].color} ${cursor}% ${cursor + pct}%`);
      cursor += pct;
    }
  });
  if (stops.length === 0) stops.push('#E2E8F0 0% 100%');
  const dominant = Object.entries(percent).reduce(
    (best, [k, v]) => (v > best[1] ? [k, v] : best),
    ['S', 0],
  );
  return (
    <div className="flex items-center gap-6">
      <div className="relative" style={{ width: 160, height: 160 }}>
        <div
          className="rounded-full"
          style={{
            width: 160,
            height: 160,
            background: `conic-gradient(${stops.join(', ')})`,
          }}
        />
        <div
          className="absolute inset-0 flex flex-col items-center justify-center rounded-full bg-white shadow-inner"
          style={{ width: 100, height: 100, margin: 'auto' }}
        >
          <span className="text-2xl font-bold" style={{ color: DISC_META[dominant[0]].color }}>{dominant[0]}</span>
          <span className="text-[10px] text-slate-500 uppercase tracking-wider">Dominant</span>
        </div>
      </div>
      <div className="space-y-2 flex-1">
        {['D', 'I', 'S', 'C'].map((k) => (
          <div key={k} className="flex items-center gap-3 text-sm">
            <span className="w-3 h-3 rounded" style={{ backgroundColor: DISC_META[k].color }} />
            <span className="font-semibold text-slate-800 w-4">{k}</span>
            <span className="text-slate-600 flex-1">{DISC_META[k].name}</span>
            <span className="font-bold text-slate-900 tabular-nums w-14 text-right">
              {(percent[k] || 0).toFixed(1)}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

const EngagementBars = ({ engagement }) => {
  const rows = Object.keys(ENGAGEMENT_LABELS).map((key) => ({
    key,
    label: ENGAGEMENT_LABELS[key],
    pct: engagement.percent?.[key] || 0,
    sampled: engagement.sampled?.[key] || 0,
  }));
  return (
    <div className="space-y-3">
      {rows.map((r) => (
        <div key={r.key}>
          <div className="flex justify-between text-xs mb-1">
            <span className="font-semibold text-slate-700">{r.label}</span>
            <span className="text-slate-500 tabular-nums">
              {r.pct.toFixed(0)}% <span className="text-slate-400">· {r.sampled} sampled</span>
            </span>
          </div>
          <div className="h-2 bg-slate-100 rounded overflow-hidden">
            <div
              className="h-full rounded"
              style={{
                width: `${Math.min(100, r.pct)}%`,
                background: 'linear-gradient(90deg, #14B8A6, #06B6D4)',
              }}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

const MoodStrip = ({ mood }) => {
  const total = Object.values(mood).reduce((a, b) => a + b, 0);
  if (!total) return null;
  return (
    <div className="flex h-3 rounded overflow-hidden">
      {Object.entries(mood).map(([k, v]) => {
        if (!v) return null;
        return (
          <div
            key={k}
            title={`${MOOD_META[k]?.label}: ${v} (${Math.round((v / total) * 100)}%)`}
            style={{ background: MOOD_META[k]?.color || '#94A3B8', flex: v }}
          />
        );
      })}
    </div>
  );
};

const CoachingHint = ({ data }) => {
  if (!data.total_calls_analysed) return null;
  const disc = data.disc_distribution;
  const dominant = disc?.dominant;
  const pct = disc?.percent?.[dominant] || 0;
  const hint = DISC_META[dominant]?.hint || '';
  const suggestions = {
    D: 'Recommend assertiveness + rapid-close training — these callers want speed and answers, not small talk.',
    I: 'Coach agents on story-based selling — these callers respond to warmth and enthusiasm, not spec sheets.',
    S: 'Emphasise active listening + trust-building — these callers move slowly and value being heard.',
    C: 'Prep agents with data sheets + pricing detail — these callers want facts, evidence, and time to think.',
  };
  return (
    <div className="rounded-xl p-4 border" style={{ borderColor: DISC_META[dominant]?.color + '55', background: DISC_META[dominant]?.color + '11' }}>
      <div className="flex items-start gap-3">
        <Brain className="w-5 h-5 mt-0.5" style={{ color: DISC_META[dominant]?.color }} />
        <div className="space-y-1">
          <p className="text-sm font-semibold text-slate-900">
            {pct.toFixed(0)}% of your callers are <span style={{ color: DISC_META[dominant]?.color }}>{dominant} — {DISC_META[dominant]?.name}</span>.
          </p>
          <p className="text-xs text-slate-600">{hint}</p>
          <p className="text-xs text-slate-800 pt-1">
            <span className="font-semibold">Coaching suggestion:</span> {suggestions[dominant]}
          </p>
        </div>
      </div>
    </div>
  );
};

const TopCallersTable = ({ callers, onCallerClick }) => (
  <div className="border border-slate-200 rounded-xl overflow-hidden bg-white">
    <table className="w-full text-sm">
      <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
        <tr>
          <th className="text-left px-4 py-2 font-semibold">Caller</th>
          <th className="text-center px-4 py-2 font-semibold">DISC</th>
          <th className="text-center px-4 py-2 font-semibold">Calls</th>
          <th className="text-left px-4 py-2 font-semibold">Dominant traits</th>
        </tr>
      </thead>
      <tbody>
        {callers.map((c) => {
          const engTop = Object.entries(c.engagement_counts || {})
            .sort((a, b) => b[1] - a[1])
            .slice(0, 3)
            .map(([k]) => ENGAGEMENT_LABELS[k])
            .filter(Boolean)
            .join(' · ');
          const disc = c.dominant_disc || 'S';
          return (
            <tr
              key={c.customer_phone}
              onClick={() => onCallerClick?.({ phone: c.customer_phone, disc, dominantTraits: engTop })}
              className="border-t border-slate-100 hover:bg-teal-50/60 cursor-pointer transition-colors"
              data-testid={`caller-row-${c.customer_phone}`}
              title={c.total_calls > 1
                ? `${c.total_calls} calls on record — click for the continuity view`
                : 'Click to open this caller\'s call'}
            >
              <td className="px-4 py-2 font-mono text-xs text-slate-700 group-hover:text-teal-700">{c.customer_phone}</td>
              <td className="px-4 py-2 text-center">
                <span
                  className="inline-block px-2 py-0.5 rounded text-xs font-bold text-white"
                  style={{ background: DISC_META[disc]?.color }}
                  title={DISC_META[disc]?.name}
                >{disc}</span>
              </td>
              <td className="px-4 py-2 text-center font-semibold tabular-nums text-slate-800">
                {c.total_calls > 1 ? (
                  <span
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-teal-100 text-teal-800"
                    title={`Called ${c.total_calls} times — click for the continuity timeline`}
                  >
                    {c.total_calls}× ↺
                  </span>
                ) : (
                  <span>{c.total_calls}</span>
                )}
              </td>
              <td className="px-4 py-2 text-xs text-slate-500">{engTop || '—'}</td>
            </tr>
          );
        })}
        {callers.length === 0 && (
          <tr>
            <td colSpan={4} className="text-center text-slate-400 italic py-6">
              No caller profiles yet — run a backfill to populate.
            </td>
          </tr>
        )}
      </tbody>
    </table>
  </div>
);

const CompatibilityCell = ({ cell, discColor }) => {
  if (!cell || cell.count === 0) {
    return <div className="text-slate-300 text-xs text-center py-2">—</div>;
  }
  const avg = cell.avg;
  // Colour intensity keyed to the DISC column's colour, mixed with a
  // score-driven alpha (higher score = fuller colour). Sub-70 scores get
  // a red tint to flag underperformance regardless of DISC column.
  let bg;
  if (avg < 55) bg = 'rgba(239, 68, 68, 0.20)';
  else if (avg < 70) bg = 'rgba(245, 158, 11, 0.20)';
  else if (avg < 85) bg = discColor + '33';
  else bg = discColor + '66';
  return (
    <div
      className={`rounded py-2 text-center text-sm font-bold ${cell.reliable ? 'text-slate-900' : 'text-slate-400 italic'}`}
      style={{ background: bg }}
      title={`${cell.count} call${cell.count === 1 ? '' : 's'}${cell.reliable ? '' : ' — small sample'}`}
    >
      {avg}
      <span className="block text-[10px] font-normal text-slate-500 tabular-nums">{cell.count} calls</span>
    </div>
  );
};

const CompatibilityMatrix = ({ data }) => {
  if (!data?.agents?.length) {
    return (
      <div className="text-center text-slate-400 italic py-8 border border-dashed border-slate-200 rounded-xl">
        Not enough data yet — needs graded calls with matching caller-intel insights.
      </div>
    );
  }
  return (
    <div className="space-y-3">
      <div className="overflow-x-auto border border-slate-200 rounded-xl bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-2 font-semibold min-w-[180px]">Agent</th>
              {data.disc_keys.map((d) => (
                <th key={d} className="px-3 py-2 font-semibold text-center">
                  <span
                    className="inline-block w-6 h-6 rounded-full text-white font-bold leading-6"
                    style={{ background: DISC_META[d].color }}
                    title={DISC_META[d].name}
                  >{d}</span>
                </th>
              ))}
              <th className="px-3 py-2 font-semibold text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            {data.agents.map((a) => {
              const profile = a.agent_disc_profile;
              const tint = profile ? DISC_META[profile]?.color : null;
              return (
                <tr
                  key={a.agent_id}
                  className="border-t border-slate-100 hover:bg-slate-50"
                  style={tint ? { background: tint + '10' } : undefined}
                >
                  <td className="px-4 py-3 align-top" style={tint ? { borderLeft: `3px solid ${tint}` } : undefined}>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-slate-800">{a.agent_name}</span>
                      {profile && (
                        <span
                          className="inline-block px-1.5 py-0.5 rounded text-[10px] font-bold text-white"
                          style={{ background: tint }}
                          title={`This agent's own DISC profile: ${DISC_META[profile]?.name}`}
                        >{profile}</span>
                      )}
                    </div>
                    {a.agent_disc_note && (
                      <p className="text-[11px] text-slate-500 mt-0.5 italic max-w-[260px] leading-tight">
                        {a.agent_disc_note}
                      </p>
                    )}
                  </td>
                  {data.disc_keys.map((d) => (
                    <td key={d} className="px-2 py-1 w-24">
                      <CompatibilityCell cell={a.cells[d]} discColor={DISC_META[d].color} />
                    </td>
                  ))}
                  <td className="px-3 py-2 text-right font-semibold text-slate-600 tabular-nums">
                    {a.total_calls}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-slate-400 italic">
        Cells with fewer than {data.min_calls} calls are shown greyed — small samples aren&apos;t reliable signals.
      </p>
      {data.insights?.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-xs font-bold text-slate-600 uppercase tracking-wider">
            Actionable pairings
          </h4>
          {data.insights.map((ins) => (
            <div
              key={ins.agent_id}
              className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm"
            >
              <span className="font-bold text-slate-900">{ins.agent_name}</span>
              <span className="text-slate-600"> scores </span>
              <span className="font-bold" style={{ color: DISC_META[ins.best_disc]?.color }}>
                {ins.best_avg} with {ins.best_disc}-types
              </span>
              <span className="text-slate-600"> but </span>
              <span className="font-bold" style={{ color: DISC_META[ins.worst_disc]?.color }}>
                {ins.worst_avg} with {ins.worst_disc}-types
              </span>
              <span className="text-slate-600"> — pair them with {DISC_META[ins.best_disc]?.name.toLowerCase()} leads.</span>
              <span className="text-[11px] text-slate-400 ml-1">
                (gap {ins.gap}pts · {ins.best_count + ins.worst_count} calls)
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const CallerIntelTab = ({ domainId }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [callers, setCallers] = useState([]);
  const [matrix, setMatrix] = useState(null);
  const [busy, setBusy] = useState(false);
  const [selectedCaller, setSelectedCaller] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = domainId ? `?domain_id=${domainId}` : '';
      const [overview, list, compat] = await Promise.all([
        axios.get(`${API}/analytics/caller-intel${q}${q ? '&' : '?'}days=30`),
        axios.get(`${API}/analytics/caller-intel/callers${q}${q ? '&' : '?'}limit=25`),
        axios.get(`${API}/analytics/caller-intel/compatibility${q}${q ? '&' : '?'}days=30&min_calls=2`),
      ]);
      setData(overview.data);
      setCallers(list.data?.callers || []);
      setMatrix(compat.data);
    } catch (e) {
      toast.error('Could not load caller intel');
    } finally {
      setLoading(false);
    }
  }, [domainId]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (enabled) => {
    if (!domainId) return;
    setBusy(true);
    try {
      await axios.patch(
        `${API}/admin/domains/${domainId}/caller-intel`,
        { enabled },
      );
      toast.success(`Caller intelligence ${enabled ? 'enabled' : 'disabled'}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Toggle failed');
    } finally {
      setBusy(false);
    }
  };

  const backfill = async () => {
    if (!domainId) return;
    setBusy(true);
    try {
      await axios.post(
        `${API}/admin/domains/${domainId}/caller-intel/backfill?days=30`,
        {},
      );
      toast.success('Backfill started — refresh in ~1 minute');
      setTimeout(load, 45000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Backfill failed');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-teal-500" />
      </div>
    );
  }

  if (!data?.enabled) {
    return (
      <div className="max-w-xl mx-auto bg-white border border-slate-200 rounded-2xl p-8 text-center space-y-4" data-testid="caller-intel-disabled">
        <div className="inline-flex w-14 h-14 items-center justify-center rounded-full bg-teal-50 mx-auto">
          <Brain className="w-7 h-7 text-teal-600" />
        </div>
        <h3 className="text-lg font-bold text-slate-900">Caller Intelligence is off</h3>
        <p className="text-sm text-slate-600">
          Turn this on to run a DISC + engagement analysis on every incoming call. The team then sees
          the mix of caller types they&apos;re dealing with and gets tailored coaching suggestions.
        </p>
        <button
          disabled={busy}
          onClick={() => toggle(true)}
          className="inline-flex items-center gap-2 bg-teal-600 hover:bg-teal-700 disabled:opacity-60 text-white px-5 py-2 rounded-lg text-sm font-semibold"
          data-testid="caller-intel-enable-btn"
        >
          <Play className="w-4 h-4" /> Enable Caller Intelligence
        </button>
      </div>
    );
  }

  const disc = data.disc_distribution || { percent: {}, counts: {} };
  const hasData = data.total_calls_analysed > 0;

  return (
    <div className="space-y-6" data-testid="caller-intel-tab">
      {/* Header + toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <Brain className="w-5 h-5 text-teal-600" /> Caller Intelligence
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            {data.total_calls_analysed} calls analysed · last {data.days} days
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            disabled={busy}
            onClick={backfill}
            className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-teal-200 bg-teal-50 hover:bg-teal-100 text-teal-800 disabled:opacity-50 inline-flex items-center gap-1.5"
            data-testid="caller-intel-backfill-btn"
          >
            <TrendingUp className="w-3.5 h-3.5" /> Backfill last 30 days
          </button>
          <button
            disabled={busy}
            onClick={() => toggle(false)}
            className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-600 disabled:opacity-50 inline-flex items-center gap-1.5"
            data-testid="caller-intel-disable-btn"
          >
            <PowerOff className="w-3.5 h-3.5" /> Disable
          </button>
        </div>
      </div>

      {!hasData && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900 flex gap-2">
          <Info className="w-4 h-4 mt-0.5 shrink-0" />
          <span>No calls have been analysed yet. Click <b>Backfill last 30 days</b> to
            classify existing calls — new calls will be auto-analysed once the toggle is on.</span>
        </div>
      )}

      {hasData && (
        <>
          <CoachingHint data={data} />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <h3 className="text-sm font-bold text-slate-700 mb-4 uppercase tracking-wider">
                DISC Distribution
              </h3>
              <DiscDonut percent={disc.percent} />
            </div>

            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <h3 className="text-sm font-bold text-slate-700 mb-4 uppercase tracking-wider">
                Engagement Traits
              </h3>
              <EngagementBars engagement={data.engagement || {}} />
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <h3 className="text-sm font-bold text-slate-700 mb-3 uppercase tracking-wider flex items-center gap-2">
              <Users className="w-4 h-4" /> Caller Mood Mix
            </h3>
            <MoodStrip mood={data.mood || {}} />
            <div className="flex flex-wrap gap-3 mt-3 text-xs">
              {Object.entries(data.mood || {}).map(([k, v]) => (
                <span key={k} className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full" style={{ background: MOOD_META[k]?.color }} />
                  <span className="text-slate-700 font-semibold">{MOOD_META[k]?.label}</span>
                  <span className="text-slate-400 tabular-nums">{v}</span>
                </span>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-sm font-bold text-slate-700 mb-3 uppercase tracking-wider flex items-center gap-2">
              <Users className="w-4 h-4" /> Top external callers
            </h3>
            <TopCallersTable
              callers={callers}
              onCallerClick={setSelectedCaller}
            />
          </div>

          <div>
            <h3 className="text-sm font-bold text-slate-700 mb-1 uppercase tracking-wider flex items-center gap-2">
              <TrendingUp className="w-4 h-4" /> Agent × Caller-DISC Compatibility
            </h3>
            <p className="text-xs text-slate-500 mb-3">
              Average grading score for each rep against each caller personality type. Use this to
              route incoming leads to the agent most likely to close them.
            </p>
            <CompatibilityMatrix data={matrix} />
          </div>
        </>
      )}

      {selectedCaller && (
        <CallerCallsModal
          phone={selectedCaller.phone}
          disc={selectedCaller.disc}
          dominantTraits={selectedCaller.dominantTraits}
          domainId={domainId}
          onClose={() => setSelectedCaller(null)}
        />
      )}
    </div>
  );
};

export default CallerIntelTab;
